import { create } from 'zustand';
import api from '../lib/api';
import type {
  ImportBatch,
  ImportConfirmation,
  SchoolMatch,
  SchoolMatchCounts,
  MatchReviewStatus,
} from '../types/import';

interface ImportState {
  // List state
  batches: ImportBatch[];
  isLoading: boolean;
  error: string | null;

  // Detail state
  currentBatch: ImportBatch | null;

  // Confirmation queue state
  confirmations: ImportConfirmation[];

  // School match state
  schoolMatches: SchoolMatch[];
  matchCounts: SchoolMatchCounts | null;
  matchFilter: MatchReviewStatus | 'all';
  matchSort: string;
  matchPagination: { page: number; limit: number; total: number };

  // Actions
  fetchBatches: () => Promise<void>;
  uploadCsvs: (source: string, files: File[]) => Promise<ImportBatch>;
  fetchBatchDetail: (batchId: string) => Promise<void>;
  fetchSchoolMatches: (batchId: string, status?: string) => Promise<void>;
  reviewMatch: (batchId: string, matchId: string, status: string, matchedOrgId?: string) => Promise<void>;
  executeImport: (batchId: string) => Promise<void>;
  bulkReview: (batchId: string, action: string, minScore?: number) => Promise<number>;
  deleteBatch: (batchId: string) => Promise<void>;
  setMatchFilter: (filter: MatchReviewStatus | 'all') => void;
  setMatchSort: (sort: string) => void;
  setMatchPage: (page: number) => void;
  pollBatchStatus: (batchId: string) => Promise<ImportBatch>;
  fetchConfirmations: (batchId: string) => Promise<void>;
  decideConfirmation: (batchId: string, confirmationId: string, decision: 'CONFIRM' | 'REJECT') => Promise<void>;
  clearError: () => void;
}

export const useImportStore = create<ImportState>((set, get) => ({
  // Initial state
  batches: [],
  isLoading: false,
  error: null,
  currentBatch: null,
  confirmations: [],
  schoolMatches: [],
  matchCounts: null,
  matchFilter: 'PENDING_REVIEW',
  matchSort: 'name' as string,
  matchPagination: { page: 1, limit: 50, total: 0 },

  /**
   * Fetch all import batches
   */
  fetchBatches: async () => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get('/api/imports');
      set({
        batches: response.data.importBatches,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch import batches:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load import batches',
      });
    }
  },

  /**
   * Upload CSV files for processing
   */
  uploadCsvs: async (source: string, files: File[]): Promise<ImportBatch> => {
    try {
      set({ isLoading: true, error: null });

      const formData = new FormData();
      formData.append('source', source);
      files.forEach((file) => {
        formData.append('files', file);
      });

      const response = await api.post('/api/imports', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      set({ isLoading: false });

      // Refresh batches list
      get().fetchBatches();

      return response.data.importBatch;
    } catch (error: any) {
      console.error('Failed to upload CSVs:', error);
      const message = error.response?.data?.message || 'Failed to upload files';
      set({ isLoading: false, error: message });
      throw new Error(message);
    }
  },

  /**
   * Fetch single batch detail
   */
  fetchBatchDetail: async (batchId: string) => {
    try {
      set({ isLoading: true, error: null });
      const response = await api.get(`/api/imports/${batchId}`);
      set({
        currentBatch: response.data.importBatch,
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch batch detail:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load batch details',
      });
    }
  },

  /**
   * Fetch school matches for a batch
   */
  fetchSchoolMatches: async (batchId: string, status?: string) => {
    try {
      set({ isLoading: true, error: null });

      const { matchPagination, matchSort } = get();
      const params = new URLSearchParams();
      params.append('page', matchPagination.page.toString());
      params.append('limit', matchPagination.limit.toString());
      params.append('sort', matchSort);
      if (status && status !== 'all') {
        params.append('status', status);
      }

      const response = await api.get(`/api/imports/${batchId}/school-matches?${params.toString()}`);

      set({
        schoolMatches: response.data.schoolMatches,
        matchCounts: response.data.counts,
        matchPagination: {
          ...matchPagination,
          total: response.data.pagination?.total || 0,
        },
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch school matches:', error);
      set({
        isLoading: false,
        error: error.response?.data?.message || 'Failed to load school matches',
      });
    }
  },

  /**
   * Review a single school match
   */
  reviewMatch: async (batchId: string, matchId: string, status: string, matchedOrgId?: string) => {
    try {
      const body: Record<string, string> = { status };
      if (matchedOrgId) {
        body.matchedOrgId = matchedOrgId;
      }

      await api.patch(`/api/imports/${batchId}/school-matches/${matchId}`, body);

      // Refresh school matches
      const { matchFilter } = get();
      await get().fetchSchoolMatches(batchId, matchFilter);
    } catch (error: any) {
      console.error('Failed to review match:', error);
      const message = error.response?.data?.message || 'Failed to update match';
      set({ error: message });
      throw new Error(message);
    }
  },

  /**
   * Execute the import for a batch
   */
  executeImport: async (batchId: string) => {
    try {
      set({ isLoading: true, error: null });
      await api.post(`/api/imports/${batchId}/execute`);
      // Refresh batch detail
      await get().fetchBatchDetail(batchId);
      set({ isLoading: false });
    } catch (error: any) {
      console.error('Failed to execute import:', error);
      const message = error.response?.data?.message || 'Failed to execute import';
      set({ isLoading: false, error: message });
      throw new Error(message);
    }
  },

  /**
   * Bulk review: confirm top matches or create unmatched
   */
  bulkReview: async (batchId: string, action: string, minScore?: number): Promise<number> => {
    try {
      set({ isLoading: true, error: null });
      const body: Record<string, unknown> = { action };
      if (minScore != null) body.minScore = minScore;
      const response = await api.post(`/api/imports/${batchId}/bulk-review`, body);
      // Refresh matches and counts
      const { matchFilter } = get();
      await get().fetchSchoolMatches(batchId, matchFilter);
      set({ isLoading: false });
      return response.data.updated;
    } catch (error: any) {
      console.error('Failed to bulk review:', error);
      const message = error.response?.data?.message || 'Failed to bulk review';
      set({ isLoading: false, error: message });
      throw new Error(message);
    }
  },

  /**
   * Delete/cancel an import batch
   */
  deleteBatch: async (batchId: string) => {
    try {
      await api.delete(`/api/imports/${batchId}`);
      // Refresh batches list
      get().fetchBatches();
    } catch (error: any) {
      console.error('Failed to delete batch:', error);
      const message = error.response?.data?.message || 'Failed to delete batch';
      set({ error: message });
      throw new Error(message);
    }
  },

  /**
   * Set match filter and re-fetch
   */
  setMatchFilter: (filter: MatchReviewStatus | 'all') => {
    const { currentBatch } = get();
    set({ matchFilter: filter, matchPagination: { ...get().matchPagination, page: 1 } });
    if (currentBatch) {
      get().fetchSchoolMatches(currentBatch.id, filter);
    }
  },

  /**
   * Set sort order and re-fetch
   */
  setMatchSort: (sort: string) => {
    const { currentBatch, matchFilter } = get();
    set({ matchSort: sort, matchPagination: { ...get().matchPagination, page: 1 } });
    if (currentBatch) {
      get().fetchSchoolMatches(currentBatch.id, matchFilter);
    }
  },

  /**
   * Set match pagination page
   */
  setMatchPage: (page: number) => {
    const { currentBatch, matchFilter } = get();
    set({ matchPagination: { ...get().matchPagination, page } });
    if (currentBatch) {
      get().fetchSchoolMatches(currentBatch.id, matchFilter);
    }
  },

  /**
   * Poll batch status until no longer PENDING/MATCHING/IMPORTING
   */
  pollBatchStatus: async (batchId: string): Promise<ImportBatch> => {
    return new Promise((resolve, reject) => {
      const poll = async () => {
        try {
          const response = await api.get(`/api/imports/${batchId}`);
          const batch: ImportBatch = response.data.importBatch;
          // Update both currentBatch and the batch in the list (for progress bar)
          const updatedBatches = get().batches.map((b) => (b.id === batch.id ? batch : b));
          set({ currentBatch: batch, batches: updatedBatches });

          if (batch.status === 'PENDING' || batch.status === 'MATCHING' || batch.status === 'IMPORTING') {
            setTimeout(poll, 2000);
          } else {
            // Refresh batches list
            get().fetchBatches();
            resolve(batch);
          }
        } catch (error: any) {
          reject(error);
        }
      };
      poll();
    });
  },

  /**
   * Fetch the batch's confirmation queue
   */
  fetchConfirmations: async (batchId: string) => {
    try {
      const response = await api.get(`/api/imports/${batchId}/confirmations`);
      set({ confirmations: response.data.confirmations });
    } catch (error: any) {
      console.error('Failed to fetch import confirmations:', error);
      set({ error: error.response?.data?.message || 'Failed to load confirmations' });
    }
  },

  /**
   * Confirm (end-date the incumbent) or reject (keep them) one queued slot
   */
  decideConfirmation: async (batchId: string, confirmationId: string, decision: 'CONFIRM' | 'REJECT') => {
    try {
      await api.post(`/api/imports/${batchId}/confirmations/${confirmationId}/decide`, { decision });
      await get().fetchConfirmations(batchId);
    } catch (error: any) {
      console.error('Failed to decide confirmation:', error);
      const message = error.response?.data?.message || 'Failed to record decision';
      set({ error: message });
      throw new Error(message);
    }
  },

  /**
   * Clear error state
   */
  clearError: () => {
    set({ error: null });
  },
}));
